import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

export function useRouteLoading(delay = 300) {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousPathRef = useRef<string | null>(null);
  const seenPaths = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!router.isReady) return;

    const onStart = (url: string) => {
      // Debounce: if navigating to same URL again within a short time, skip
      if (seenPaths.current.has(url)) {
        return;
      }

      seenPaths.current.add(url);
      previousPathRef.current = url;

      timer.current = setTimeout(() => {
        setLoading(true);
      }, delay);
    };

    const onEnd = (url: string) => {
      seenPaths.current.delete(url);
      if (timer.current) clearTimeout(timer.current);
      setLoading(false);
    };

    router.events.on('routeChangeStart', onStart);
    router.events.on('routeChangeComplete', onEnd);
    router.events.on('routeChangeError', onEnd);

    return () => {
      if (timer.current) clearTimeout(timer.current);
      router.events.off('routeChangeStart', onStart);
      router.events.off('routeChangeComplete', onEnd);
      router.events.off('routeChangeError', onEnd);
    };
  }, [router, delay]);

  return loading;
}
